/**
 * Centralized route configuration for upgrade prompt display.
 *
 * This config determines where upgrade prompts (banners, modals, toasts) should be shown.
 *
 * Logic:
 * - If `allowedPages` has items, prompt shows ONLY on those pages (prefix match)
 * - If `allowedPages` is empty, prompt shows on all pages EXCEPT those in `excludedPages`
 * - Pages are matched using prefix (startsWith), so `/dashboard` matches `/dashboard/settings`
 *
 * Future: This can be migrated to database-driven rules by adding `showOnPages` and `hideOnPages`
 * fields to the `UpgradePrompt` model.
 */
export const UPGRADE_PROMPT_ROUTE_CONFIG = {
	/**
	 * Modal prompts configuration
	 * For now: Only show modals on /dashboard page
	 * Later: Will be customizable per prompt
	 */
	modal: {
		// Only show modals on these pages
		allowedPages: ['/dashboard'],
		// Never show modals on these pages (even if in allowedPages)
		excludedPages: [],
	},

	/**
	 * Banner prompts configuration
	 * For now: Show banners on all pages except upgrade, signup flow, and login pages
	 * Later: Will be customizable per prompt
	 */
	banner: {
		// Show everywhere (empty = no restrictions)
		allowedPages: [],
		// Never show banners on these pages
		excludedPages: [
			'/upgrade', // Already on upgrade page
			'/sign-up', // Signup flow - don't interrupt
			'/sign-up/create-account',
			'/sign-up/checkout',
			'/sign-up/complete',
			'/dashboard',
			'/login', // Auth pages - don't interrupt
		],
	},

	/**
	 * Toast prompts configuration
	 * For now: Show toasts on all pages except upgrade and auth
	 * Later: Will be customizable per prompt
	 */
	toast: {
		// Show everywhere
		allowedPages: [],
		// Never show toasts on these pages
		excludedPages: [
			'/upgrade', // Already on upgrade page
			'/login',
			'/logout',
			'/forgot-password',
			'/reset-password',
		],
	},
} as const;

export type PromptType = keyof typeof UPGRADE_PROMPT_ROUTE_CONFIG;
